import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Users, Building2, Activity, Shield, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleLabels = {
  admin: "Administrateur",
  avocat: "Avocat",
  juriste: "Juriste",
  relecteur: "Relecteur",
  stagiaire: "Stagiaire"
};

const roleBadgeColor = {
  admin: "bg-purple-100 text-purple-800 border-purple-200",
  avocat: "bg-blue-100 text-blue-800 border-blue-200",
  juriste: "bg-green-100 text-green-800 border-green-200",
  relecteur: "bg-amber-100 text-amber-800 border-amber-200",
  stagiaire: "bg-slate-100 text-slate-800 border-slate-200"
};

export default function CabinetManagement({ user, cabinet, onLogout }) {
  const [cabinetInfo, setCabinetInfo] = useState(null);
  const [cabinetUsers, setCabinetUsers] = useState([]);
  const [clients, setClients] = useState([]);  // NEW
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pendingTemplates, setPendingTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);  // NEW
  const [showAddClientModal, setShowAddClientModal] = useState(false);  // NEW
  const [editingClient, setEditingClient] = useState(null);  // NEW

  const isAdmin = user.role === "admin";

  useEffect(() => {
    loadCabinetData();
  }, []);

  const loadCabinetData = async () => {
    setLoading(true);
    const token = localStorage.getItem('lawliet_token');
    
    try {
      // Load cabinet info
      const cabinetRes = await axios.get(`${API}/cabinet/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCabinetInfo(cabinetRes.data);

      // Load users
      const usersRes = await axios.get(`${API}/cabinet/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCabinetUsers(usersRes.data);

      // Load clients - NEW
      const clientsRes = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(clientsRes.data.clients || []);

      // Load stats (admin only)
      if (isAdmin) {
        const statsRes = await axios.get(`${API}/cabinet/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(statsRes.data);

        // Load audit logs
        const logsRes = await axios.get(`${API}/cabinet/audit-logs?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAuditLogs(logsRes.data.logs);

        // Load pending templates
        const tplRes = await axios.get(`${API}/templates/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingTemplates(tplRes.data.templates || []);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm("Voulez-vous vraiment désactiver cet utilisateur ?")) return;

    const token = localStorage.getItem('lawliet_token');
    try {
      await axios.post(`${API}/cabinet/users/${userId}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utilisateur désactivé");
      loadCabinetData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    const token = localStorage.getItem('lawliet_token');
    try {
      await axios.post(`${API}/cabinet/users/${userId}/change-role?new_role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Rôle modifié");
      loadCabinetData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleApproveTemplate = async (templateId, approved) => {
    const token = localStorage.getItem('lawliet_token');
    try {
      await axios.post(`${API}/templates/${templateId}/approve`,
        { approved, admin_comment: approved ? "Approuvé" : "Rejeté" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approved ? "Template approuvé et disponible pour le cabinet" : "Template rejeté");
      loadCabinetData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // NEW - Client Management Functions
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Voulez-vous vraiment archiver ce client ?")) return;

    const token = localStorage.getItem('lawliet_token');
    try {
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client archivé");
      loadCabinetData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleSaveClient = async (clientData) => {
    const token = localStorage.getItem('lawliet_token');
    try {
      if (editingClient) {
        // Update existing
        await axios.put(`${API}/clients/${editingClient.id}`, clientData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Client mis à jour");
      } else {
        // Create new
        await axios.post(`${API}/clients`, clientData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Client créé");
      }
      setShowAddClientModal(false);
      setEditingClient(null);
      loadCabinetData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6" data-testid="cabinet-management">
        <div>
          <h1 className="text-3xl font-bold text-blue-900" style={{ fontFamily: 'EB Garamond, serif' }}>
            Gestion du Cabinet
          </h1>
          <p className="text-slate-600 mt-1">{cabinetInfo?.name}</p>
        </div>

        {isAdmin && stats && (
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Utilisateurs</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {stats.users.current}/{stats.users.max}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-900 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Documents analysés</p>
                    <p className="text-2xl font-bold text-blue-900">{stats.documents_analyzed}</p>
                  </div>
                  <Activity className="h-8 w-8 text-blue-900 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Plan</p>
                    <p className="text-2xl font-bold text-blue-900 capitalize">{stats.plan}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-900 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="users" className="w-full">
          <TabsList>
            <TabsTrigger value="users" data-testid="users-tab">Utilisateurs</TabsTrigger>
            <TabsTrigger value="clients" data-testid="clients-tab">Clients</TabsTrigger>
            <TabsTrigger value="info" data-testid="info-tab">Informations</TabsTrigger>
            {isAdmin && <TabsTrigger value="templates" data-testid="templates-tab">
              Templates {pendingTemplates.length > 0 && (
                <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-4 text-center">
                  {pendingTemplates.length}
                </span>
              )}
            </TabsTrigger>}
            {isAdmin && <TabsTrigger value="audit" data-testid="audit-tab">Journaux d'audit</TabsTrigger>}
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Membres du cabinet</CardTitle>
                    <CardDescription>{cabinetUsers.length} utilisateur(s)</CardDescription>
                  </div>
                  {isAdmin && (
                    <Button onClick={() => setShowAddMemberModal(true)} className="gap-2">
                      <Users className="h-4 w-4" />
                      Inviter membre
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {cabinetUsers.map((member) => (
                    <Card key={member.id} className="border">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div>
                                <p className="font-semibold text-slate-900">{member.full_name}</p>
                                <p className="text-sm text-slate-600">{member.email}</p>
                                {member.job_title && (
                                  <p className="text-xs text-slate-500 mt-1">{member.job_title}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Badge className={`${roleBadgeColor[member.role]} border`}>
                              {roleLabels[member.role]}
                            </Badge>
                            {isAdmin && member.id !== user.id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeactivateUser(member.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Désactiver
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Clients Tab - NEW */}
          <TabsContent value="clients" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Gestion des Clients</CardTitle>
                    <CardDescription>
                      Base de données centralisée de vos clients pour rédaction rapide
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowAddClientModal(true)} className="gap-2">
                    <Users className="h-4 w-4" />
                    Nouveau client
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {clients.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm mb-4">Aucun client enregistré</p>
                    <Button onClick={() => setShowAddClientModal(true)} size="sm">
                      Ajouter votre premier client
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {clients.map(client => (
                      <div key={client.id} className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 transition">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Building2 className="h-4 w-4 text-blue-600" />
                              <h3 className="font-semibold text-slate-900">{client.name}</h3>
                              <Badge variant="outline" className="text-xs">{client.legal_form}</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                              <p><span className="font-medium">SIREN:</span> {client.siren}</p>
                              <p><span className="font-medium">RCS:</span> {client.rcs}</p>
                              <p><span className="font-medium">Capital:</span> {client.capital} €</p>
                              <p><span className="font-medium">Représentant:</span> {client.representative_name}</p>
                              <p className="col-span-2"><span className="font-medium">Adresse:</span> {client.address}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingClient(client);
                                setShowAddClientModal(true);
                              }}
                            >
                              Éditer
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteClient(client.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              Archiver
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="info">
            <Card>
              <CardHeader>
                <CardTitle>Informations du cabinet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600">Nom</p>
                  <p className="font-semibold">{cabinetInfo?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Email</p>
                  <p className="font-semibold">{cabinetInfo?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Plan</p>
                  <Badge className="capitalize">{cabinetInfo?.plan}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Utilisateurs maximum</p>
                  <p className="font-semibold">{cabinetInfo?.max_users}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  <p className="text-sm text-slate-600">Journaux d'audit activés</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="templates">
              <Card>
                <CardHeader>
                  <CardTitle>Validation des Templates Cabinet</CardTitle>
                  <CardDescription>
                    Templates soumis par des membres pour partage à tout le cabinet
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingTemplates.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">Aucun template en attente de validation</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pendingTemplates.map((tpl) => (
                        <Card key={tpl.id} className="border-amber-200 bg-amber-50">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-900">
                                  {typeof tpl.name === 'object' ? tpl.name.fr || tpl.name.en : tpl.name}
                                </p>
                                <p className="text-sm text-slate-600 mt-1">
                                  {typeof tpl.description === 'object' ? tpl.description.fr || tpl.description.en : tpl.description}
                                </p>
                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                  <span>Catégorie : {tpl.category}</span>
                                  <span>•</span>
                                  <span>{tpl.clauses?.length || 0} clause(s)</span>
                                </div>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleApproveTemplate(tpl.id, true)}
                                  data-testid={`approve-template-btn`}
                                >
                                  Approuver
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => handleApproveTemplate(tpl.id, false)}
                                  data-testid={`reject-template-btn`}
                                >
                                  Rejeter
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="audit">
              <Card>
                <CardHeader>
                  <CardTitle>Journaux d'audit</CardTitle>
                  <CardDescription>Dernières actions effectuées</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-2">
                      {auditLogs.map((log, idx) => (
                        <div key={idx} className="p-3 border rounded-lg bg-slate-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{log.action}</p>
                              <p className="text-xs text-slate-600">{log.user_email}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant={log.status === "success" ? "default" : "destructive"}>
                                {log.status}
                              </Badge>
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(log.timestamp).toLocaleString('fr-FR')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
        
        {/* Add Member Modal */}
        {showAddMemberModal && (
          <AddMemberModal
            onClose={() => setShowAddMemberModal(false)}
            onSave={async (email) => {
              const token = localStorage.getItem('lawliet_token');
              try {
                await axios.post(`${API}/cabinet/invite`, { email }, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Invitation envoyée");
                setShowAddMemberModal(false);
              } catch (error) {
                toast.error(error.response?.data?.detail || "Erreur");
              }
            }}
          />
        )}

        {/* Add/Edit Client Modal */}
        {showAddClientModal && (
          <ClientFormModal
            client={editingClient}
            onClose={() => {
              setShowAddClientModal(false);
              setEditingClient(null);
            }}
            onSave={handleSaveClient}
          />
        )}
      </div>
    </Layout>
  );
}

// Add Member Modal Component
function AddMemberModal({ onClose, onSave }) {
  const [email, setEmail] = useState("");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Inviter un membre</h3>
          <p className="text-sm text-slate-600 mt-1">Envoyez une invitation par email</p>
        </div>
        <div className="p-6">
          <Label className="text-sm mb-2 block">Email du membre *</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="avocat@cabinet.fr"
          />
        </div>
        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={() => onSave(email)} disabled={!email.includes('@')}>
            Envoyer invitation
          </Button>
        </div>
      </div>
    </div>
  );
}

// Client Form Modal Component
function ClientFormModal({ client, onClose, onSave }) {
  const [formData, setFormData] = useState(client || {
    name: "", legal_form: "SAS", rcs: "Paris", siren: "", capital: "",
    address: "", representative_name: "", representative_title: "Directeur Général", designation: ""
  });

  const update = (field, value) => setFormData({...formData, [field]: value});

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">{client ? "Modifier client" : "Nouveau client"}</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Raison sociale *</Label>
              <Input value={formData.name || ''} onChange={(e) => update('name', e.target.value)} placeholder="TechCorp SAS" />
            </div>
            <div>
              <Label>Forme juridique *</Label>
              <select value={formData.legal_form || 'SAS'} onChange={(e) => update('legal_form', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                <option value="SAS">SAS</option>
                <option value="SARL">SARL</option>
                <option value="SA">SA</option>
                <option value="SCI">SCI</option>
                <option value="EURL">EURL</option>
                <option value="Auto-entrepreneur">Auto-entrepreneur</option>
              </select>
            </div>
            <div>
              <Label>Capital (€) *</Label>
              <Input value={formData.capital || ''} onChange={(e) => update('capital', e.target.value)} placeholder="100000" />
            </div>
            <div>
              <Label>SIREN *</Label>
              <Input value={formData.siren || ''} onChange={(e) => update('siren', e.target.value)} placeholder="123456789" maxLength={9} />
            </div>
            <div>
              <Label>RCS *</Label>
              <Input value={formData.rcs || ''} onChange={(e) => update('rcs', e.target.value)} placeholder="Paris" />
            </div>
            <div className="col-span-2">
              <Label>Adresse du siège social *</Label>
              <Input value={formData.address || ''} onChange={(e) => update('address', e.target.value)} placeholder="10 Avenue des Champs-Élysées, 75008 Paris" />
            </div>
            <div>
              <Label>Représentant légal *</Label>
              <Input value={formData.representative_name || ''} onChange={(e) => update('representative_name', e.target.value)} placeholder="Jean Dupont" />
            </div>
            <div>
              <Label>Fonction *</Label>
              <Input value={formData.representative_title || ''} onChange={(e) => update('representative_title', e.target.value)} placeholder="Directeur Général" />
            </div>
            <div className="col-span-2">
              <Label>Désignation contractuelle</Label>
              <Input value={formData.designation || ''} onChange={(e) => update('designation', e.target.value)} placeholder="la Société, le Client, le Prestataire..." />
            </div>
          </div>
        </div>
        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={() => onSave(formData)} disabled={!formData.name || !formData.siren || !formData.representative_name}>
            {client ? "Mettre à jour" : "Créer client"}
          </Button>
        </div>
      </div>
    </div>
  );
}
